import { Suspense, lazy, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";

import { Page, Skeleton, Stack } from "@/components/ui";

import {
  draftKeys,
  isUuidShaped,
  slugForDraftId,
  useDraftByAddress,
} from "../features/studio/lib/draft";
import type { StepId } from "../features/studio/lib/steps";

const StudioEditor = lazy(() =>
  import("../features/studio/StudioEditor").then((module) => ({
    default: module.StudioEditor,
  })),
);

interface StudioSearch {
  readonly step?: StepId | undefined;
}

const STEP_IDS: readonly StepId[] = ["start", "data", "behavior", "scenes", "connect", "review"];

function asStep(value: unknown): StepId | undefined {
  return typeof value === "string" && (STEP_IDS as readonly string[]).includes(value)
    ? (value as StepId)
    : undefined;
}

export const Route = createFileRoute("/console/studio/$draft")({
  validateSearch: (search: Record<string, unknown>): StudioSearch => ({
    step: asStep(search.step),
  }),
  /** A bookmarked `/console/studio/<uuid>` link still resolves, but the address bar never keeps
   * the uuid: this looks the id up, once, and moves the bar to the slug before anything renders. */
  beforeLoad: async ({ params, search }) => {
    if (!isUuidShaped(params.draft)) return;
    const slug = await slugForDraftId(params.draft);
    if (slug !== undefined && slug !== params.draft) {
      throw redirect({ to: "/console/studio/$draft", params: { draft: slug }, search });
    }
  },
  component: Designer,
});

function Loading() {
  return (
    <Page title="Studio">
      <Stack gap={4}>
        <Skeleton height={32} />
        <Skeleton height={240} />
      </Stack>
    </Page>
  );
}

function Designer() {
  const { draft: address } = Route.useParams();
  const { step } = Route.useSearch();
  const navigate = Route.useNavigate();
  const client = useQueryClient();
  const resolved = useDraftByAddress(address);

  useEffect(() => {
    const found = resolved.data;
    if (found !== undefined) client.setQueryData(draftKeys.detail(found.draft_id), found);
  }, [resolved.data, client]);

  if (resolved.isLoading) return <Loading />;

  return (
    <Suspense fallback={<Loading />}>
      <StudioEditor
        draftId={resolved.data?.draft_id ?? address}
        step={step ?? "start"}
        onStepChange={(next) => {
          void navigate({ search: { step: next } });
        }}
      />
    </Suspense>
  );
}
